import { useEffect, useState } from "react";
import { api } from "../../helpers/apiCore";
import { endpoints } from "../../helpers/Endpoints";
import { Alert, Button, Card, Spinner } from "react-bootstrap";
import errorInfoIcon from "../../assets/icons/error_info.svg";
import FormInput from "../../components/FormInput";


type PermissionCategory = {
	category: string;
	permissions: string[];
};

const CreateRole = () => {
	const [permissionsList, setPermissionsList] = useState<PermissionCategory[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showAlert, setShowAlert] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [formData, setFormData] = useState({
		name: "",
		permissions: {} as { [key: string]: string[] },
	});

	const capitalizeFirstLetter = (string: string) => {
		if (!string) return '';
		return string.charAt(0).toUpperCase() + string.slice(1);
	};

	const flattenPermissions = (backendData: any): PermissionCategory[] => {
		if (!backendData || !Array.isArray(backendData)) {
			console.warn('Invalid permissions data received:', backendData);
			return [];
		}

		return backendData.map(item => {
			const category = Object.keys(item)[0];
			const permissions = item[category];
			return {
				category,
				permissions: Array.isArray(permissions) ? permissions : []
			};
		});
	};

	useEffect(() => {
		const fetchPermissionsList = async () => {
			setIsLoading(true);
			try {
				api.setAuthorization(api.getLoggedInUser());
				const response = await api.get(endpoints.getPermissionsList, {});
				
				let permissionsData = null;
				if (response?.data?.data) {
					permissionsData = response.data.data;
				} else if (response?.data) {
					permissionsData = response.data;
				} else if (response) {
					permissionsData = response;
				}
				
				const flattenedPermissions = flattenPermissions(permissionsData);
				
				setPermissionsList(flattenedPermissions);
			} catch (error: any) {
				console.error("Error fetching permissions:", error);
				setError("Failed to load permissions");
				setShowAlert(true);
			} finally {
				setIsLoading(false);
			}
		};
		fetchPermissionsList();
		setShowAlert(false);
	}, []);

	const handleCheckboxChange = (category: string, permission: string, isChecked: boolean) => {
		setShowAlert(false);
		setFormData(prevFormData => {
			const updatedPermissions = { ...prevFormData.permissions };
			if (isChecked) {
				if (updatedPermissions[category]) {
					updatedPermissions[category].push(permission);
				} else {
					updatedPermissions[category] = [permission];
				}
			} else {
				updatedPermissions[category] = updatedPermissions[category].filter(perm => perm !== permission);
				if (updatedPermissions[category].length === 0) {
					delete updatedPermissions[category];
				}
			}
			return { ...prevFormData, permissions: updatedPermissions };
		});
	};

	const isPermissionSelected = (category: string, permission: string): boolean => {
		return formData.permissions[category]?.includes(permission) || false;
	};

	const isAllCategorySelected = (category: string): boolean => {
		const categoryPermissions = permissionsList.find(p => p.category === category)?.permissions || [];
		const selectedPermissions = formData.permissions[category] || [];
		return categoryPermissions.length > 0 && categoryPermissions.every(perm => selectedPermissions.includes(perm));
	};

	const handleSelectAllCategory = (category: string, isChecked: boolean) => {
		setShowAlert(false);
		const categoryPermissions = permissionsList.find(p => p.category === category)?.permissions || [];
		
		setFormData(prevFormData => {
			const updatedPermissions = { ...prevFormData.permissions };
			if (isChecked) {
				updatedPermissions[category] = [...categoryPermissions];
			} else {
				delete updatedPermissions[category];
			}
			return { ...prevFormData, permissions: updatedPermissions };
		});
	};

	const handleCreateRole = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsSubmitting(true);

		const permissionsArray = Object.keys(formData.permissions).map(category => {
			return { [category]: formData.permissions[category] };
		});

		try {
			api.setAuthorization(api.getLoggedInUser());
			const roleData = {
				name: formData.name,
				permissions: permissionsArray,
			};
			const response = await api.create(endpoints.createRole, roleData);
			setSuccessMessage("Role created successfully.");
			setShowAlert(true);
			setFormData({
				name: '',
				permissions: {} as { [key: string]: string[] },
			});

			console.log(response, 'response');
		} catch (error: any) {
			const message = error?.response?.data?.message || 'Unable to create role please try again';
			setError(message);
			setShowAlert(true);
			setSuccessMessage(null);
		} finally {
			setIsSubmitting(false);
		}
	};

	const isFormValid = formData.name.trim() && Object.keys(formData.permissions).length > 0;

	return (
		<Card className="form-container text-left mt-3">
			<Card.Body className="create-form-body">
				<form onSubmit={handleCreateRole}>
					<p>Create New Role</p>

					<div className="card-form-row">
						<FormInput
							label="Role Title"
							name="name"						
							className="custom-input"
							value={formData.name}
							onChange={e => {
								setFormData({ ...formData, name: e.target.value });
								setShowAlert(false);
							}}
							required
						/>
						
						<div className="permissions-section mt-4">
							<label>Permissions</label>
							{isLoading ? (
								<div className="text-center py-4">
									<Spinner animation="border" size="sm" role="status" />
									<span className="ms-2">Loading permissions...</span>
								</div>
							) : (
								<div className="permissions-list">
									{permissionsList.map((permissionCategory) => (
										<div key={permissionCategory.category} className="permission-category">
											<div className="permissions-list-header d-flex align-items-center">
												<input
													type="checkbox"
													id={`select-all-${permissionCategory.category}`}
													className="form-check-input me-2"
													checked={isAllCategorySelected(permissionCategory.category)}
													onChange={(e) => handleSelectAllCategory(permissionCategory.category, e.target.checked)}
												/>

												<label htmlFor={`select-all-${permissionCategory.category}`}>
													{capitalizeFirstLetter(permissionCategory.category)} <br />
												</label>
											</div>
											
											<div className="permissions-grid">
												{permissionCategory.permissions.map((permission) => (
													<div key={`${permissionCategory.category}-${permission}`} className="form-check">
														<input
															type="checkbox"
															id={`${permissionCategory.category}-${permission}`}
															className="form-check-input"
															checked={isPermissionSelected(permissionCategory.category, permission)}
															onChange={(e) => handleCheckboxChange(permissionCategory.category, permission, e.target.checked)}
														/>
														
														<label htmlFor={`${permissionCategory.category}-${permission}`}>
															{capitalizeFirstLetter(permission)}
														</label>
													</div>
												))}
											</div>
										</div>
									))}
								</div>
							)}
						</div>

						{showAlert && (
							<Alert
								variant={error ? 'danger' : 'success'}
								className={error ? 'alert-error' : 'alert-success'}
							>
								{error && <img src={errorInfoIcon} alt="error" />}
								{error ? error : successMessage}
							</Alert>
						)}

						<Button
							type="submit"
							className="custom-button mt-3"
							disabled={isSubmitting || !isFormValid}
						>
							{isSubmitting ? <Spinner animation="border" size="sm" /> : "Create Role"}
						</Button>
					</div>
				</form>
			</Card.Body>
		</Card>
	);
};

export default CreateRole;